import type { EmojiPickerSection } from "@/src/design_system/button_and_controls/emoji_picker/types/panel";
import { EMOJI_CATEGORY, EMOJI_DATA_CATEGORIES, EMOJI_SKIN_TONE } from "@/src/shared/emoji/constants";
import type { EmojiCategory, EmojiSkinTone } from "@/src/shared/emoji/types";
import { filterEmojis, resolveEmojiUnicodes } from "@/src/shared/emoji/utils";

interface BuildEmojiSectionsOptions {
  category: EmojiCategory;
  recentEmojis: unknown;
  search: string;
  skinTone?: EmojiSkinTone;
}

const groupEmojisByCategory = (emojis: ReturnType<typeof filterEmojis>): EmojiPickerSection[] => {
  return EMOJI_DATA_CATEGORIES.map(category => ({
    category,
    emojis: emojis.filter(emoji => emoji.category === category),
  })).filter(section => section.emojis.length > 0);
};

export const buildEmojiSections = ({
  category,
  recentEmojis,
  search,
  skinTone = EMOJI_SKIN_TONE.Neutral,
}: BuildEmojiSectionsOptions): EmojiPickerSection[] => {
  const normalizedRecentEmojis = resolveEmojiUnicodes(recentEmojis).map(emoji => emoji.unicode);
  const filterOptions = { recentEmojis: normalizedRecentEmojis, search, skinTone };

  if (search.trim()) {
    return groupEmojisByCategory(filterEmojis({ ...filterOptions, category: EMOJI_CATEGORY.All }));
  }

  if (category === EMOJI_CATEGORY.All) {
    const sections = groupEmojisByCategory(filterEmojis({ ...filterOptions, category }));
    const recentItems = filterEmojis({ ...filterOptions, category: EMOJI_CATEGORY.Recent });

    if (recentItems.length) sections.unshift({ category: EMOJI_CATEGORY.Recent, emojis: recentItems });
    return sections;
  }

  const emojis = filterEmojis({ ...filterOptions, category });
  return emojis.length ? [{ category, emojis }] : [];
};

export const limitEmojiSections = (sections: EmojiPickerSection[], limit: number): EmojiPickerSection[] => {
  const itemCounts = sections.map(() => 0);
  const totalEmojiCount = sections.reduce((total, section) => total + section.emojis.length, 0);
  let remaining = Math.min(Math.max(limit, 0), totalEmojiCount);

  while (remaining > 0) {
    let addedItem = false;

    sections.forEach((section, index) => {
      if (remaining === 0 || itemCounts[index] >= section.emojis.length) return;

      itemCounts[index] += 1;
      remaining -= 1;
      addedItem = true;
    });

    if (!addedItem) break;
  }

  return sections
    .map((section, index) => ({
      category: section.category,
      emojis: section.emojis.slice(0, itemCounts[index]),
    }))
    .filter(section => section.emojis.length > 0);
};
